import { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';

// ─── Icons ─────────────────────────────────────────────────────────────────────

const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

// ─── Helpers ───────────────────────────────────────────────────────────────────

function toMapsUrl(input) {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) return trimmed;
  // Plain address — encode into a Google Maps search URL
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
}

// ─── Inline venue form ─────────────────────────────────────────────────────────

const EMPTY_FORM = { name: '', city: '', state: 'NY', maps_url: '', website_url: '' };

function VenueForm({ initial = EMPTY_FORM, onSave, onCancel, saving, saveStatus }) {
  const [form, setForm] = useState(initial);
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const inputClass = "font-poppins text-sm text-white placeholder-white/20 rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200";
  const inputStyle = { backgroundColor: '#2A2A2A', border: '1px solid rgba(255,255,255,0.1)' };
  const onFocus = e => { e.target.style.borderColor = 'rgba(247,148,29,0.5)'; };
  const onBlur  = e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; };

  return (
    <div
      className="rounded-2xl flex flex-col gap-5 p-6 mt-2"
      style={{ backgroundColor: '#252525', border: '1px solid rgba(247,148,29,0.25)' }}
    >
      {/* Location Name */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Location Name</label>
        <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
          placeholder="e.g. The Crown Hotel" className={inputClass} style={inputStyle}
          onFocus={onFocus} onBlur={onBlur} />
      </div>

      {/* City + State side by side */}
      <div className="flex gap-3">
        <div className="flex flex-col gap-1.5 flex-1">
          <label className="font-poppins font-semibold text-white text-sm">City</label>
          <input type="text" value={form.city} onChange={e => set('city', e.target.value)}
            placeholder="e.g. Albany" className={inputClass} style={inputStyle}
            onFocus={onFocus} onBlur={onBlur} />
        </div>
        <div className="flex flex-col gap-1.5" style={{ width: 80 }}>
          <label className="font-poppins font-semibold text-white text-sm">State</label>
          <input type="text" value={form.state} onChange={e => set('state', e.target.value)}
            placeholder="NY" maxLength={10} className={inputClass} style={inputStyle}
            onFocus={onFocus} onBlur={onBlur} />
        </div>
      </div>

      {/* Maps / Address */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Location</label>
        <p className="font-poppins text-white/40 text-xs -mt-0.5">Paste a Google Maps URL or a street address — either works.</p>
        <input type="text" value={form.maps_url} onChange={e => set('maps_url', e.target.value)}
          placeholder="e.g. 448 Madison Ave, Albany NY  or  https://maps.google.com/..."
          className={inputClass} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
      </div>

      {/* Website */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Bar/Venue Website (optional)</label>
        <input type="url" value={form.website_url} onChange={e => set('website_url', e.target.value)}
          placeholder="https://..." className={inputClass} style={inputStyle}
          onFocus={onFocus} onBlur={onBlur} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => onSave(form)}
          disabled={saving}
          className="font-poppins font-bold text-sm px-6 py-2.5 rounded-xl transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: saving ? 'rgba(247,148,29,0.4)' : '#F7941D',
            color: '#1A1A1A',
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={e => { if (!saving) e.currentTarget.style.backgroundColor = '#ffb83f'; }}
          onMouseLeave={e => { if (!saving) e.currentTarget.style.backgroundColor = '#F7941D'; }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="font-poppins font-medium text-sm px-6 py-2.5 rounded-xl transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
        >
          Cancel
        </button>

        {saveStatus === 'success' && (
          <div className="flex items-center gap-1.5">
            <span style={{ color: '#4ade80' }}>✓</span>
            <p className="font-poppins text-sm font-medium" style={{ color: '#4ade80' }}>Saved!</p>
          </div>
        )}
        {saveStatus === 'validation' && (
          <p className="font-poppins text-sm font-medium" style={{ color: '#f87171' }}>
            Please fill in the location name and city.
          </p>
        )}
        {saveStatus === 'duplicate' && (
          <p className="font-poppins text-sm font-medium" style={{ color: '#f87171' }}>
            This location already exists. If this is a different location, try adding the city or neighborhood to the name.
          </p>
        )}
        {saveStatus === 'error' && (
          <p className="font-poppins text-sm font-medium" style={{ color: '#f87171' }}>
            Something went wrong. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Single venue card ─────────────────────────────────────────────────────────

function getLocationLabel(mapsUrl) {
  if (!mapsUrl) return null;
  try {
    const url = new URL(mapsUrl);
    // If we generated this from a plain address, decode it back to readable text
    if (url.searchParams.has('query')) return url.searchParams.get('query');
  } catch {}
  // Raw URL — strip protocol and truncate
  return mapsUrl.replace(/^https?:\/\//, '').slice(0, 52) + (mapsUrl.length > 58 ? '…' : '');
}

function VenueCard({ item, onEdit, onDelete }) {
  const locationLabel = getLocationLabel(item.maps_url);

  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-3"
      style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      {/* Name + city + location */}
      <div className="flex items-start gap-3">
        <span style={{ color: '#F7941D', marginTop: 2, flexShrink: 0 }}>
          <LocationIcon />
        </span>
        <div className="flex flex-col gap-0.5 min-w-0">
          <p className="font-poppins font-bold text-white text-base leading-tight">
            {item.name || <span style={{ color: 'rgba(255,255,255,0.3)' }}>Unnamed location</span>}
          </p>
          {(item.city || item.state) && (
            <p className="font-poppins text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {[item.city, item.state].filter(Boolean).join(', ')}
            </p>
          )}
          {locationLabel ? (
            <p className="font-poppins text-xs mt-0.5 break-all" style={{ color: 'rgba(255,255,255,0.3)' }}>
              {locationLabel}
            </p>
          ) : (
            <p className="font-poppins text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.18)' }}>
              No location added yet
            </p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div
        className="flex items-center gap-2 pt-1"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <button
          type="button"
          onClick={() => onEdit(item)}
          className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(247,148,29,0.1)', color: '#F7941D', border: '1px solid rgba(247,148,29,0.2)' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(247,148,29,0.2)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(247,148,29,0.1)'}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(item.id)}
          className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(248,113,113,0.08)', color: 'rgba(248,113,113,0.7)', border: '1px solid rgba(248,113,113,0.15)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.18)'; e.currentTarget.style.color = '#f87171'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.08)'; e.currentTarget.style.color = 'rgba(248,113,113,0.7)'; }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── Delete confirmation modal ─────────────────────────────────────────────────

function DeleteModal({ venueName, onConfirm, onCancel, deleting }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
    >
      <div
        className="rounded-2xl p-7 flex flex-col gap-5 w-full max-w-sm"
        style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="flex flex-col gap-2">
          <h2 className="font-poppins font-bold text-white text-lg">Delete location?</h2>
          <p className="font-poppins text-white/50 text-sm leading-relaxed">
            You're about to delete{' '}
            <span className="text-white font-semibold">{venueName || 'this location'}</span>.
          </p>
          <p
            className="font-poppins text-sm leading-relaxed px-4 py-3 rounded-xl"
            style={{ backgroundColor: 'rgba(248,113,113,0.08)', color: '#f87171', border: '1px solid rgba(248,113,113,0.2)' }}
          >
            ⚠️ Deleting this location will also remove it from the schedule. This cannot be undone.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="font-poppins font-bold text-sm px-6 py-2.5 rounded-xl focus:outline-none transition-all duration-200"
            style={{
              backgroundColor: deleting ? 'rgba(248,113,113,0.4)' : '#f87171',
              color: '#fff',
              cursor: deleting ? 'not-allowed' : 'pointer',
            }}
          >
            {deleting ? 'Deleting…' : 'Yes, delete it'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="font-poppins font-medium text-sm px-6 py-2.5 rounded-xl focus:outline-none transition-all duration-200"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main section ──────────────────────────────────────────────────────────────

function VenuesSection() {
  const [venues, setVenues]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editingId, setEditingId]   = useState(null); // null | 'new' | <id>
  const [editInitial, setEditInitial] = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleting, setDeleting]     = useState(false);
  const [sortBy, setSortBy]         = useState('name'); // 'name' | 'city'

  // ── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    const { data, error } = await supabase
      .from('venues')
      .select('*');
    if (!error && data) setVenues(data);
    setLoading(false);
  }

  // ── Open forms ───────────────────────────────────────────────────────────
  function handleEdit(item) {
    setEditInitial({ name: item.name, city: item.city, state: item.state ?? 'NY', maps_url: item.maps_url ?? '', website_url: item.website_url ?? '' });
    setEditingId(item.id);
    setSaveStatus(null);
  }

  function handleAddNew() {
    setEditInitial(EMPTY_FORM);
    setEditingId('new');
    setSaveStatus(null);
  }

  function handleCancel() {
    setEditingId(null);
    setSaveStatus(null);
  }

  // ── Save ─────────────────────────────────────────────────────────────────
  async function handleSave(form) {
    if (!form.name.trim() || !form.city.trim()) {
      setSaveStatus('validation');
      return;
    }

    if (editingId === 'new') {
      const isDuplicate = venues.some(v =>
        v.name.trim().toLowerCase() === form.name.trim().toLowerCase() &&
        v.city.trim().toLowerCase() === form.city.trim().toLowerCase() &&
        (v.state ?? '').trim().toLowerCase() === form.state.trim().toLowerCase()
      );
      if (isDuplicate) {
        setSaveStatus('duplicate');
        return;
      }
    }
    setSaving(true);
    setSaveStatus(null);

    const payload = {
      name: form.name.trim(),
      city: form.city.trim(),
      state: form.state.trim() || null,
      maps_url: toMapsUrl(form.maps_url),
      website_url: form.website_url.trim() || null,
    };

    let error;
    if (editingId === 'new') {
      ({ error } = await supabase.from('venues').insert(payload));
    } else {
      ({ error } = await supabase.from('venues').update(payload).eq('id', editingId));
    }

    setSaving(false);
    if (error) {
      setSaveStatus('error');
    } else {
      setSaveStatus('success');
      await fetchAll();
      setTimeout(() => { setEditingId(null); setSaveStatus(null); }, 1200);
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────
  async function handleDeleteConfirm() {
    setDeleting(true);
    const { error } = await supabase.from('venues').delete().eq('id', deleteTarget.id);
    setDeleting(false);
    if (!error) {
      setVenues(prev => prev.filter(v => v.id !== deleteTarget.id));
      if (editingId === deleteTarget.id) setEditingId(null);
    }
    setDeleteTarget(null);
  }

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div className="h-7 w-36 rounded-lg bg-white/10" />
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 rounded-2xl bg-white/5" />
        ))}
      </div>
    );
  }

  // ── Sorted list ──────────────────────────────────────────────────────────
  const sortedVenues = [...venues].sort((a, b) => {
    if (sortBy === 'city') {
      const state = (a.state ?? '').localeCompare(b.state ?? '');
      if (state !== 0) return state;
      const city = (a.city ?? '').localeCompare(b.city ?? '');
      return city !== 0 ? city : (a.name ?? '').localeCompare(b.name ?? '');
    }
    return (a.name ?? '').localeCompare(b.name ?? '');
  });

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6">

      {/* Header + Add button */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-poppins font-bold text-white text-2xl">Locations</h1>
          <p className="font-poppins text-white/40 text-sm mt-1">
            {venues.length} location{venues.length !== 1 ? 's' : ''} — these appear in your schedule and on the map.
          </p>
        </div>
        {editingId !== 'new' && (
          <button
            type="button"
            onClick={handleAddNew}
            className="font-poppins font-bold text-sm px-5 py-2.5 rounded-xl transition-all duration-200 focus:outline-none flex items-center gap-2 flex-shrink-0"
            style={{ backgroundColor: '#F7941D', color: '#1A1A1A' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ffb83f'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F7941D'}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
            Add New Location
          </button>
        )}
      </div>

      {/* New venue form */}
      {editingId === 'new' && (
        <VenueForm
          initial={editInitial}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          saveStatus={saveStatus}
        />
      )}

      {/* Sort controls */}
      {venues.length > 1 && (
        <div className="flex items-center gap-2">
          {[{ key: 'name', label: 'Sort by Name' }, { key: 'city', label: 'Sort by City/State' }].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setSortBy(key)}
              className="font-poppins text-xs font-semibold px-4 py-2 rounded-lg transition-all duration-200 focus:outline-none"
              style={{
                backgroundColor: sortBy === key ? 'rgba(247,148,29,0.15)' : 'rgba(255,255,255,0.05)',
                color: sortBy === key ? '#F7941D' : 'rgba(255,255,255,0.4)',
                border: `1px solid ${sortBy === key ? 'rgba(247,148,29,0.4)' : 'rgba(255,255,255,0.08)'}`,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      {venues.length === 0 && editingId !== 'new' ? (
        <div
          className="rounded-2xl p-10 flex flex-col items-center justify-center gap-3"
          style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="font-poppins text-white/30 text-sm text-center">
            No locations yet. Hit "Add New Location" to get started!
          </p>
        </div>
      ) : sortBy === 'city' ? (
        // ── Grouped by state ──────────────────────────────────────────────
        (() => {
          const groups = [];
          sortedVenues.forEach(item => {
            const key = item.state || '—';
            const last = groups[groups.length - 1];
            if (last && last.state === key) last.items.push(item);
            else groups.push({ state: key, items: [item] });
          });
          return (
            <div className="flex flex-col gap-5">
              {groups.map(({ state, items: groupItems }) => (
                <div
                  key={state}
                  className="rounded-2xl overflow-hidden"
                  style={{ border: '1px solid rgba(247,148,29,0.18)', backgroundColor: '#1c1c1c' }}
                >
                  {/* State header */}
                  <div
                    className="px-5 py-2.5 flex items-center gap-2"
                    style={{ borderBottom: '1px solid rgba(247,148,29,0.18)', backgroundColor: 'rgba(247,148,29,0.06)' }}
                  >
                    <span className="font-poppins font-bold text-xs tracking-widest uppercase" style={{ color: '#F7941D' }}>
                      {state}
                    </span>
                    <span className="font-poppins text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
                      {groupItems.length} location{groupItems.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {/* Cards grid */}
                  <div className="grid gap-3 sm:grid-cols-2 p-4">
                    {groupItems.map(item => (
                      <div key={item.id}>
                        <VenueCard
                          item={item}
                          onEdit={handleEdit}
                          onDelete={id => setDeleteTarget({ id, name: item.name })}
                        />
                        {editingId === item.id && (
                          <VenueForm
                            initial={editInitial}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            saving={saving}
                            saveStatus={saveStatus}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      ) : (
        // ── Flat list (Sort by Name) ───────────────────────────────────────
        <div className="grid gap-3 sm:grid-cols-2">
          {sortedVenues.map(item => (
            <div key={item.id}>
              <VenueCard
                item={item}
                onEdit={handleEdit}
                onDelete={id => setDeleteTarget({ id, name: item.name })}
              />
              {editingId === item.id && (
                <VenueForm
                  initial={editInitial}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  saving={saving}
                  saveStatus={saveStatus}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <DeleteModal
          venueName={deleteTarget.name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}

    </div>
  );
}

export default VenuesSection;
